import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { PAYROLL_STATUS, deriveStepStatus } from './usePayrollStatus';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_LinkRecebimentosInput.ts';
import '../contracts/Payroll_RevenueConciliationCompleteInput';
import '../contracts/Payroll_RevenueConciliationUnlinkRecebimentoInput';
import '../contracts/Payroll_RevenueConciliationCompleteResponse';
import '../contracts/Payroll_RevenueConciliationLinkRecebimentosResponse';
import '../contracts/Payroll_RevenueConciliationRecebimentosResponse';
import '../contracts/Payroll_RevenueConciliationSummaryResponse';
import '../contracts/Payroll_RevenueConciliationUnlinkRecebimentoResponse';

import type { Payroll_LinkRecebimentosInput } from '../contracts/Payroll_LinkRecebimentosInput';
/**
 * Derive UI status from numeric period status and conciliation data
 * Uses centralized deriveStepStatus from usePayrollStatus.js
 *
 * @param {number} periodStatus - The period's main status (0-5, -1)
 * @param {number} count - Number of linked recebimentos
 * @returns {string} 'pending' | 'in_progress' | 'completed'
 */
const deriveRevenueConciliationStatus = (periodStatus: any, count: any) => {
  const linkedCount = parseInt(count) || 0;
  log('useRevenueConciliation', 'deriveRevenueConciliationStatus', 'Deriving status', {
    periodStatus,
    linkedCount,
    threshold: PAYROLL_STATUS.REVENUE
  });
  return deriveStepStatus(periodStatus, PAYROLL_STATUS.REVENUE, linkedCount > 0);
};

/**
 * Composable for Revenue Conciliation operations
 * Handles revenue conciliation for payroll periods - linking recebimentos to periods
 */
export function useRevenueConciliation() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Get revenue conciliation summary for a payroll period
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Summary with status, amount, count
   */
  const getRevenueConciliationSummary = async (periodId: number) => {
    log('useRevenueConciliation', 'getRevenueConciliationSummary', 'Fetching summary', { periodId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/revenue_conciliation_summary/${periodId}`, {
        responseContract: 'Payroll_RevenueConciliationSummaryResponse'
      });
      log('useRevenueConciliation', 'getRevenueConciliationSummary', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty revenue conciliation summary response');
        }
        const data = response.dados;

        return {
          // Derive UI status from numeric period status and count
          status: deriveRevenueConciliationStatus(data.period_status, data.count),
          amount: data.amount,
          count: data.count,
          recebimentos: [] as unknown[]
        };
      } else {
        throw new Error(response.error || 'Failed to fetch revenue conciliation summary');
      }
    } catch (err) {
      log('useRevenueConciliation', 'getRevenueConciliationSummary', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Link recebimentos to payroll period via junction table
   *
   * HYBRID ARCHITECTURE:
   * - Links recebimentos to payment_rule_executions based on attribution
   * - operador_user_id → commission_over_gross_revenue_executed executions
   * - seller_user_id → commission_over_gross_revenue_sold_* executions
   * - A single recebimento can be linked to multiple executions (Kevin + João)
   *
   * @param {number} periodId - Payroll period ID
   * @param {Array<number>} recebimentoIds - Array of recebimento IDs to link
   * @returns {Promise<Object>} Result with linked_count, already_claimed_count, no_execution_count
   */
  const linkRecebimentosToPeriod = async (periodId: any, recebimentoIds: any) => {
    log('useRevenueConciliation', 'linkRecebimentosToPeriod', 'Linking recebimentos', {
      periodId,
      count: recebimentoIds.length
    });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Payroll_LinkRecebimentosInput = { recebimento_ids: recebimentoIds };
      validate('Payroll_LinkRecebimentosInput', payload);
      const response = await httpService.post(`/payroll/revenue_conciliation_link_recebimentos/${periodId}`, payload, {
        responseContract: 'Payroll_RevenueConciliationLinkRecebimentosResponse'
      });
      log('useRevenueConciliation', 'linkRecebimentosToPeriod', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty link recebimentos response');
        }
        const data = response.dados;
        log('useRevenueConciliation', 'linkRecebimentosToPeriod', 'Linking results', {
          linked_count: data.linked_count,
          already_claimed_count: data.already_claimed_count,
          no_execution_count: data.no_execution_count
        });
        return data;
      } else {
        throw new Error(response.error || 'Failed to link recebimentos');
      }
    } catch (err) {
      log('useRevenueConciliation', 'linkRecebimentosToPeriod', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Unlink a single recebimento from payroll period via junction table
   *
   * Removes all links for this recebimento from the period (may remove multiple
   * rows if the recebimento was linked to multiple executions).
   *
   * @param {number} periodId - Payroll period ID
   * @param {number} recebimentoId - Recebimento ID to unlink
   * @returns {Promise<Object>} Result with deleted_count, remaining_linked_count
   */
  const unlinkRecebimentoFromPeriod = async (periodId: number | string, recebimentoId: number | string) => {
    log('useRevenueConciliation', 'unlinkRecebimentoFromPeriod', 'Unlinking recebimento', {
      periodId,
      recebimentoId
    });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(
        `/payroll/revenue_conciliation_unlink_recebimento/${periodId}/${recebimentoId}`,
        {},
        {
          inputContract: 'Payroll_RevenueConciliationUnlinkRecebimentoInput',
          responseContract: 'Payroll_RevenueConciliationUnlinkRecebimentoResponse'
        }
      );
      log('useRevenueConciliation', 'unlinkRecebimentoFromPeriod', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty unlink recebimento response');
        }
        const data = response.dados;
        log('useRevenueConciliation', 'unlinkRecebimentoFromPeriod', 'Unlink results', {
          deleted_count: data.deleted_count,
          remaining_linked_count: data.remaining_linked_count
        });
        return data;
      } else {
        throw new Error(response.error || 'Failed to unlink recebimento');
      }
    } catch (err) {
      log('useRevenueConciliation', 'unlinkRecebimentoFromPeriod', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Complete revenue conciliation - calculates total and updates status
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Final summary with calculated amount
   */
  const completeRevenueConciliation = async (periodId: number) => {
    log('useRevenueConciliation', 'completeRevenueConciliation', 'Completing conciliation', { periodId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/payroll/revenue_conciliation_complete/${periodId}`, {}, {
        inputContract: 'Payroll_RevenueConciliationCompleteInput',
        responseContract: 'Payroll_RevenueConciliationCompleteResponse'
      });
      log('useRevenueConciliation', 'completeRevenueConciliation', 'Response', response);

      if (response.success) {
        if (!response.dados) {
          throw new Error(response.error || 'Empty complete revenue conciliation response');
        }
        const data = response.dados;
        return {
          status: 'completed',
          amount: data.amount,
          count: data.count
        };
      } else {
        throw new Error(response.error || 'Failed to complete revenue conciliation');
      }
    } catch (err) {
      log('useRevenueConciliation', 'completeRevenueConciliation', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get all recebimentos linked to a payroll period
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Array>} Array of linked recebimentos
   */
  const getLinkedRecebimentos = async (periodId: number) => {
    log('useRevenueConciliation', 'getLinkedRecebimentos', 'Fetching linked recebimentos', { periodId });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/payroll/revenue_conciliation_recebimentos/${periodId}`, {
        responseContract: 'Payroll_RevenueConciliationRecebimentosResponse'
      });
      log('useRevenueConciliation', 'getLinkedRecebimentos', 'Response', response);

      if (response.success) {
        const recebimentos = response.dados ?? [];

        // Fetch formas de pagamento lookup
        const { useFormasPagamento } = await import('./useFormasPagamento');
        const { listarFormasPagamento } = useFormasPagamento();
        const formasPagamento = (await listarFormasPagamento()) as Array<{ forma_pagamento_id: number | string; forma_pagamento_nome: string }>;

        // Create lookup map for formas de pagamento
        const formasPagamentoMap: any = {};
        formasPagamento.forEach((fp) => {
          formasPagamentoMap[fp.forma_pagamento_id] = fp.forma_pagamento_nome;
        });

        // Fetch users lookup
        const { useUsers } = await import('./useUsers');
        const { fetchUsers } = useUsers();
        const users = (await fetchUsers()) as Array<{ sub?: string; user_id?: string; name?: string; email?: string; username?: string }>;

        // Create lookup map for users
        const usersMap: any = {};
        users.forEach((user) => {
          const displayName = user.name || user.email || user.username || 'Desconhecido';
          if (user.sub) {
            usersMap[user.sub] = displayName;
          }
          if (user.user_id) {
            usersMap[user.user_id] = displayName;
          }
        });

        // Enrich recebimentos with lookup data (consistent with useRecebimento.js)
        return recebimentos.map((r: any) => {
          const formaPagamentoId = r.recebimento_forma_pagamento_id || 0;
          const operadorId = r.compromisso_user_id || '';

          return {
            ...r,
            recebimento_valor_display: (r.recebimento_valor || 0) / 100,
            forma_pagamento_nome: formasPagamentoMap[formaPagamentoId] || 'Sem forma de pagamento',
            operador_nome: usersMap[operadorId] || 'Sem operador'
          };
        });
      } else {
        throw new Error(response.error || 'Failed to fetch linked recebimentos');
      }
    } catch (err) {
      log('useRevenueConciliation', 'getLinkedRecebimentos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    getRevenueConciliationSummary,
    linkRecebimentosToPeriod,
    unlinkRecebimentoFromPeriod,
    completeRevenueConciliation,
    getLinkedRecebimentos
  };
}
